"use client";

import {
  ReactNode,
  useEffect,
  useState,
  createContext,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useSimpleAuth } from "../auth/SimpleAuthProvider";
import { 
  Organization, 
  OrganizationMember, 
  OrganizationRole,
  OrganizationInvitation
} from "@wtc/types";

// ============================================================================
// ORGANIZATION PROVIDER - MULTI-TENANT MANAGEMENT
// ============================================================================
//
// PURPOSE: Manage organization context, switching, and membership.
//
// ============================================================================

interface OrganizationContextType {
  organizations: Organization[];
  currentOrg: Organization | null;
  setCurrentOrg: (org: Organization | null) => void;
  members: OrganizationMember[];
  invitations: OrganizationInvitation[];
  isLoading: boolean;
  error: string | null;
  refreshOrganizations: () => Promise<void>;
  refreshMembers: () => Promise<void>;
  refreshInvitations: () => Promise<void>;
  inviteUser: (email: string, role: OrganizationRole) => Promise<string | null>;
  removeMember: (userId: string) => Promise<boolean>;
  updateMemberRole: (userId: string, role: OrganizationRole) => Promise<boolean>;
  cancelInvitation: (invitationId: string) => Promise<boolean>;
  isOrgAdmin: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined
);

interface OrganizationProviderProps {
  children: ReactNode;
  supabaseClient: any;
}

export function OrganizationProvider({
  children,
  supabaseClient,
}: OrganizationProviderProps) {
  const { user, isAuthenticated } = useSimpleAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrgState] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Persistence for current org selection (localStorage)
  useEffect(() => {
    if (typeof window !== 'undefined' && organizations.length > 0 && !currentOrg) {
      const savedOrgId = localStorage.getItem('wtc_current_org_id');
      if (savedOrgId) {
        const found = organizations.find(o => o.id === savedOrgId);
        if (found) {
          setCurrentOrgState(found);
        } else {
          setCurrentOrgState(organizations[0]);
        }
      } else {
        setCurrentOrgState(organizations[0]);
      }
    }
  }, [organizations]);

  const setCurrentOrg = useCallback((org: Organization | null) => {
    setCurrentOrgState(org);
    if (typeof window !== 'undefined') {
      if (org) {
        localStorage.setItem('wtc_current_org_id', org.id);
      } else {
        localStorage.removeItem('wtc_current_org_id');
      }
    }
  }, []);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const refreshOrganizations = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    
    setIsLoading(true);
    try {
      const { data, error: fetchError } = await supabaseClient
        .from("organization_members")
        .select(`
          organization:organizations (
            id,
            name,
            created_at,
            updated_at
          )
        `)
        .eq("user_id", user.id);

      if (fetchError) throw fetchError;
      
      const orgs = (data as any[]).map(d => d.organization).filter(Boolean);
      setOrganizations(orgs);
    } catch (err: any) {
      console.error("Error fetching organizations:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user, isAuthenticated, supabaseClient]);

  const refreshMembers = useCallback(async () => {
    if (!currentOrg) return;

    try {
      const { data, error: fetchError } = await supabaseClient
        .from("organization_members")
        .select(`
          organization_id,
          user_id,
          role,
          created_at,
          user:app_users (
            email,
            first_name,
            last_name
          )
        `)
        .eq("organization_id", currentOrg.id);

      if (fetchError) throw fetchError;
      setMembers(data as OrganizationMember[]);
    } catch (err: any) {
      console.error("Error fetching members:", err);
    }
  }, [currentOrg, supabaseClient]);

  const refreshInvitations = useCallback(async () => {
    if (!currentOrg) return;

    try {
      const { data, error: fetchError } = await supabaseClient
        .from("organization_invitations")
        .select(`*`)
        .eq("organization_id", currentOrg.id)
        .is("accepted_at", null);

      if (fetchError) throw fetchError;
      setInvitations(data as OrganizationInvitation[]);
    } catch (err: any) {
      console.error("Error fetching invitations:", err);
    }
  }, [currentOrg, supabaseClient]);

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  const inviteUser = useCallback(async (email: string, role: OrganizationRole) => {
    if (!currentOrg) return null;
    
    try {
      const { data, error: rpcError } = await supabaseClient.rpc(
        "invite_user_to_organization",
        {
          p_email: email,
          p_role: role,
          p_org_id: currentOrg.id
        }
      );

      if (rpcError) throw rpcError;
      
      await refreshInvitations();
      return data as string;
    } catch (err: any) {
      console.error("Error inviting user:", err);
      setError(err.message);
      return null;
    }
  }, [currentOrg, supabaseClient, refreshInvitations]);

  const removeMember = useCallback(async (userId: string) => {
    if (!currentOrg) return false;

    try {
      const { error: deleteError } = await supabaseClient
        .from("organization_members")
        .delete()
        .eq("organization_id", currentOrg.id)
        .eq("user_id", userId);

      if (deleteError) throw deleteError;
      
      await refreshMembers();
      return true;
    } catch (err: any) {
      console.error("Error removing member:", err);
      return false;
    }
  }, [currentOrg, supabaseClient, refreshMembers]);

  const updateMemberRole = useCallback(async (userId: string, role: OrganizationRole) => {
    if (!currentOrg) return false;

    try {
      const { error: updateError } = await supabaseClient
        .from("organization_members")
        .update({ role })
        .eq("organization_id", currentOrg.id)
        .eq("user_id", userId);

      if (updateError) throw updateError;
      
      await refreshMembers();
      return true;
    } catch (err: any) {
      console.error("Error updating member role:", err);
      return false;
    }
  }, [currentOrg, supabaseClient, refreshMembers]);

  const cancelInvitation = useCallback(async (invitationId: string) => {
    try {
      const { error: deleteError } = await supabaseClient
        .from("organization_invitations")
        .delete()
        .eq("id", invitationId);

      if (deleteError) throw deleteError;
      
      await refreshInvitations();
      return true;
    } catch (err: any) {
      console.error("Error cancelling invitation:", err);
      return false;
    }
  }, [supabaseClient, refreshInvitations]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (isAuthenticated) {
      refreshOrganizations();
    } else {
      setOrganizations([]);
      setCurrentOrg(null);
      setMembers([]);
      setInvitations([]);
    }
  }, [isAuthenticated, refreshOrganizations, setCurrentOrg]);

  useEffect(() => {
    if (currentOrg) {
      refreshMembers();
      refreshInvitations();
    }
  }, [currentOrg, refreshMembers, refreshInvitations]);

  // ============================================================================
  // COMPUTED
  // ============================================================================

  const isOrgAdmin = useMemo(() => {
    if (!currentOrg || !user) return false;
    const member = members.find(m => m.user_id === user.id);
    return member?.role === 'admin';
  }, [currentOrg, user, members]);

  const contextValue: OrganizationContextType = {
    organizations,
    currentOrg,
    setCurrentOrg,
    members,
    invitations,
    isLoading,
    error,
    refreshOrganizations,
    refreshMembers,
    refreshInvitations,
    inviteUser,
    removeMember,
    updateMemberRole,
    cancelInvitation,
    isOrgAdmin,
  };

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}
